import {FC} from 'react';
import {useForm} from 'react-hook-form';
import {TextFieldElement} from 'react-hook-form-mui';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button, DialogActions, DialogContent} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import {object, string} from 'yup';

import {getErrorMessageFromApiError} from '@shared/api';
import {Dialog, DialogTitle} from '@shared/components';
import {useAddOnboardingGeoConfig} from '../../apiHooks';
import {OnboardingType} from '../../types';

interface FormValues {
  countryCode: string;
  config: string;
}

const v8nSchema = object().shape({
  countryCode: string().required().max(2),
  config: string().required(),
});

interface Props {
  open: boolean;
  onClose: () => void;
  type: OnboardingType;
}

export const AddGeoConfigDialog: FC<Props> = ({open, onClose, type}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: addGeoConfig, isPending: isAdding} =
    useAddOnboardingGeoConfig();

  const {control, handleSubmit, reset} = useForm<FormValues>({
    resolver: yupResolver(v8nSchema),
    defaultValues: {countryCode: '', config: ''},
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    try {
      await addGeoConfig({
        type,
        countryCode: values.countryCode.trim().toUpperCase(),
        config: values.config.trim(),
      });
      enqueueSnackbar('Geo config added successfully', {variant: 'success'});
      handleClose();
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not add geo config. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle
          title="Add geo config"
          onClose={handleClose}
          sx={{px: 3, pt: 3}}
        />

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minWidth: 380,
            px: 3,
            pt: 1,
          }}
        >
          <TextFieldElement
            name="countryCode"
            label="Country code (e.g. US)"
            control={control}
            fullWidth
          />

          <TextFieldElement
            name="config"
            label="Config URL"
            control={control}
            fullWidth
          />
        </DialogContent>

        <DialogActions sx={{px: 3, pb: 3}}>
          <Button variant="outlined" onClick={handleClose} disabled={isAdding}>
            Cancel
          </Button>
          <Button type="submit" disabled={isAdding}>
            Add
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
